import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppContext } from '../containers/appContextContainer';
import { KontaktinformasjonWeb, NavnWeb, SoeknadContainerWeb, SoeknadstypeWeb } from '../generated-types';
import errorTypes from '../util/gtm/errorTypes';
import restClient from '../util/web-http-client';

export const useUpdateLaantakere = (
    soeknadContainer: SoeknadContainerWeb | undefined,
    successCallback?: () => void,
) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateLaantakere'],
        mutationFn: (kontaktinformasjon: KontaktinformasjonWeb) =>
            restClient.updateKontaktinformasjon(kontaktinformasjon),
        onSuccess: (data: KontaktinformasjonWeb) => {
            if (soeknadContainer) {
                soeknadContainer.laantaker = data.laantakere;
                queryClient.setQueryData(['soeknad'], soeknadContainer);
            }
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({
                errorType: errorTypes.UPDATE_LAANTAKERE,
                errorException: error as RequestExceptionWithResponse,
            });
        },
    });
};

export const useKontaktinformasjon = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['kontaktinformasjon'],
        queryFn: () => restClient.getKontaktinformasjon(soeknadtype),
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_KONTAKTINFORMASJON,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useUpdateKontaktinformasjon = (
    kontaktinformasjon: KontaktinformasjonWeb | undefined,
    successCallback?: () => void,
) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateKontaktinformasjon'],
        mutationFn: (kontaktinformasjon: KontaktinformasjonWeb) =>
            restClient.updateKontaktinformasjon(kontaktinformasjon),
        onSuccess: (data: KontaktinformasjonWeb) => {
            if (kontaktinformasjon) {
                queryClient.setQueryData(['kontaktinformasjon'], data);
            }
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({
                errorType: errorTypes.UPDATE_KONTAKTINFORMASJON,
                errorException: error as RequestExceptionWithResponse,
            });
        },
    });
};

export const usePersonNavn = (navn: NavnWeb) => {
    return useQuery({
        queryKey: ['personNavn', navn.fornavn, navn.etternavn, navn.foedselsnummer],
        queryFn: () => restClient.getPersonNavn(navn),
        enabled: navn.foedselsnummer?.value?.length === 11 && navn.fornavn?.length > 0 && navn.etternavn?.length > 0,
        retry: 1,
    });
};

export const useGetBankkonfigurasjon = () => {
    return useQuery({
        queryKey: ['bankkonfig'],
        queryFn: () => restClient.getBankkonfigurasjon(),
    });
};
